import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Chip, Skeleton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip,
  useTheme, alpha,
} from '@mui/material';
import {
  Refresh, TrendingUp, TrendingDown, ShowChart,
} from '@mui/icons-material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ReferenceLine, ReferenceArea,
} from 'recharts';
import { gamesApi } from '../services/apiService';

const fmtPct = (v) => (v != null && !isNaN(v)) ? `${Math.round(v * 100)}%` : '—';

// Custom tooltip for the WPA chart
const WPATooltip = ({ active, payload, teams }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 1, p: 1.5, boxShadow: 3, maxWidth: 280,
    }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
        {d.halfInning === 'top' ? '▲' : '▼'} {d.inning}{d.inning === 1 ? 'st' : d.inning === 2 ? 'nd' : d.inning === 3 ? 'rd' : 'th'}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary', mb: 0.75, lineHeight: 1.3 }}>
        {d.description || 'Game Start'}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {teams?.home?.abbreviation || 'Home'} Win%
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: d.homeWinProb > 0.5 ? '#06ffa5' : '#ff6b6b' }}>
            {fmtPct(d.homeWinProb)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>WPA</Typography>
          <Typography variant="body2" fontWeight={700} sx={{
            color: d.wpa > 0 ? '#06ffa5' : d.wpa < 0 ? '#ff6b6b' : 'text.secondary',
          }}>
            {d.wpa > 0 ? '+' : ''}{(d.wpa * 100).toFixed(1)}%
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Score</Typography>
          <Typography variant="body2" fontWeight={600}>
            {d.awayScore}-{d.homeScore}
          </Typography>
        </Box>
      </Box>
      {d.batter && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
          {d.batter} vs {d.pitcher} {d.onBase !== '○○○' ? `• ${d.onBase}` : ''}
        </Typography>
      )}
    </Box>
  );
};

/**
 * WinProbabilityChart — displays live/historical win probability timeline
 * and a table of top WPA plays.
 *
 * Props:
 *  - gameId: MLB game PK
 *  - isLive: whether the game is currently in progress (enables auto-refresh)
 *  - teams: { home: { abbreviation }, away: { abbreviation } }
 *  - compact: boolean, if true shows minimal version (for ScoreboardTicker)
 */
const WinProbabilityChart = ({ gameId, isLive = false, teams, compact = false }) => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!gameId) return;
    try {
      if (!data) setLoading(true);
      setError(null);
      const result = await gamesApi.getWinProbability(gameId);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    if (!gameId) { setLoading(false); return; }
    fetchData();
    if (isLive) {
      refreshRef.current = setInterval(fetchData, 30000); // 30s refresh for live
    }
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, [fetchData, isLive]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={compact ? 60 : 250} sx={{ borderRadius: 2, mb: 1 }} />
        {!compact && <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />}
      </Box>
    );
  }

  if (error || !data?.wpaTimeline?.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {error ? 'Failed to load win probability' : 'No play data available yet'}
        </Typography>
        <IconButton size="small" onClick={fetchData} sx={{ mt: 1 }}>
          <Refresh fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  const { wpaTimeline, topPlays, currentWinProbability } = data;
  const tms = data.teams || teams || {};

  // Chart data: convert homeWinProb to percentage
  const chartData = wpaTimeline.map((p, i) => ({
    ...p,
    homeWinProbPct: Math.round(p.homeWinProb * 1000) / 10, // 0-100 with 1 decimal
    idx: i,
    label: p.playIndex < 0 ? 'Start' :
      `${p.halfInning === 'top' ? '▲' : '▼'}${p.inning}`,
  }));

  // Find inning boundaries for reference lines
  const inningBoundaries = [];
  chartData.forEach((d, i) => {
    if (i === 0) return;
    const prev = chartData[i - 1];
    if (d.inning !== prev.inning || d.halfInning !== prev.halfInning) {
      inningBoundaries.push({
        idx: i,
        label: `${d.halfInning === 'top' ? '▲' : '▼'}${d.inning}`,
        isNewInning: d.inning !== prev.inning && d.halfInning === 'top',
      });
    }
  });

  // Compact mode: just a small sparkline-style chart
  if (compact) {
    const homeWP = currentWinProbability?.home ?? 0.5;
    return (
      <Box sx={{ width: '100%', height: 50 }}>
        <ResponsiveContainer width="100%" height={50}>
          <AreaChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
            <defs>
              <linearGradient id={`wpGrad-${gameId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#06ffa5" stopOpacity={0.4} />
                <stop offset="50%" stopColor="#888" stopOpacity={0.05} />
                <stop offset="100%" stopColor="#ff6b6b" stopOpacity={0.4} />
              </linearGradient>
            </defs>
            <ReferenceLine y={50} stroke={alpha(theme.palette.text.primary, 0.15)} strokeDasharray="2 2" />
            <Area
              type="monotone" dataKey="homeWinProbPct" stroke={homeWP > 0.5 ? '#06ffa5' : '#ff6b6b'}
              fill={`url(#wpGrad-${gameId})`} strokeWidth={1.5} dot={false} isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  // Full chart mode
  const homeWP = currentWinProbability?.home ?? 0.5;
  const homeAbbr = tms.home?.abbreviation || 'Home';
  const awayAbbr = tms.away?.abbreviation || 'Away';

  return (
    <Box>
      {/* Current WP Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShowChart sx={{ fontSize: 20, color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" fontWeight={700}>Win Probability</Typography>
          {isLive && (
            <Chip label="LIVE" size="small" color="success" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700, animation: 'pulse 2s infinite' }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}>
              {awayAbbr}
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{
              color: homeWP < 0.5 ? '#06ffa5' : homeWP > 0.5 ? '#ff6b6b' : 'text.primary',
              fontSize: '1rem',
            }}>
              {Math.round((1 - homeWP) * 100)}%
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>vs</Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}>
              {homeAbbr}
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{
              color: homeWP > 0.5 ? '#06ffa5' : homeWP < 0.5 ? '#ff6b6b' : 'text.primary',
              fontSize: '1rem',
            }}>
              {Math.round(homeWP * 100)}%
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchData}><Refresh sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main WPA Chart */}
      <Box sx={{
        border: '1px solid', borderColor: 'divider', borderRadius: 2,
        p: 1, mb: 2, bgcolor: alpha(theme.palette.background.default, 0.5),
      }}>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: -10 }}>
            <defs>
              <linearGradient id="wpGradFull" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#06ffa5" stopOpacity={0.3} />
                <stop offset="50%" stopColor={theme.palette.background.paper} stopOpacity={0} />
                <stop offset="100%" stopColor="#ff6b6b" stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.06)} />
            <XAxis
              dataKey="idx" type="number" tick={false} axisLine={false} tickLine={false}
            />
            <YAxis
              domain={[0, 100]} ticks={[0, 25, 50, 75, 100]}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              axisLine={false} tickLine={false}
              tickFormatter={(v) => `${v}%`}
            />
            <RTooltip content={<WPATooltip teams={tms} />} />
            <ReferenceLine y={50} stroke={alpha(theme.palette.text.primary, 0.2)} strokeDasharray="4 4" label="" />

            {/* Inning boundary lines */}
            {inningBoundaries.filter(b => b.isNewInning).map((b, i) => (
              <ReferenceLine
                key={i} x={b.idx}
                stroke={alpha(theme.palette.text.primary, 0.1)}
                strokeDasharray="2 4"
              />
            ))}

            <Area
              type="monotone" dataKey="homeWinProbPct"
              stroke={theme.palette.primary.main}
              fill="url(#wpGradFull)"
              strokeWidth={2} dot={false}
              isAnimationActive={!isLive}
              animationDuration={1000}
            />
          </AreaChart>
        </ResponsiveContainer>
        {/* Axis labels */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mt: -0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#ff6b6b', fontWeight: 600 }}>
            ← {awayAbbr} favored
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {chartData.length - 1} plays
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#06ffa5', fontWeight: 600 }}>
            {homeAbbr} favored →
          </Typography>
        </Box>
      </Box>

      {/* Top WPA Plays Table */}
      {topPlays && topPlays.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, fontSize: '0.8rem' }}>
            Biggest Plays by Win Probability Impact
          </Typography>
          <TableContainer sx={{ maxHeight: 200 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>Inn</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>Play</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>Batter</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>WPA</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.5 }}>WP After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topPlays.map((p, i) => (
                  <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.4, whiteSpace: 'nowrap' }}>
                      {p.halfInning === 'top' ? '▲' : '▼'}{p.inning}
                    </TableCell>
                    <TableCell sx={{
                      fontSize: '0.75rem', py: 0.4, maxWidth: 180,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {p.event || p.description?.split('.')[0]}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.4, fontWeight: 600 }}>
                      {p.batter?.split(' ').pop()}
                    </TableCell>
                    <TableCell align="center" sx={{
                      fontSize: '0.75rem', py: 0.4, fontWeight: 700,
                      color: p.wpa > 0 ? '#06ffa5' : '#ff6b6b',
                    }}>
                      {p.wpa > 0 ? '+' : ''}{(p.wpa * 100).toFixed(1)}%
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.4 }}>
                      {homeAbbr} {Math.round(p.homeWinProb * 100)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default WinProbabilityChart;
